import React, { useState, useEffect, useRef } from 'react';
import { gameAPI, SavedSong as FirebaseSavedSong } from '../../games/services/GameAPI';
import { GAME_REGISTRY } from '../../games/models/GameRegistry';
import defaultGameImage from '../../../shared/assets/images/games/default-game.png';
import '../styles/FallAlbumSection.css';

interface FallAlbumSectionProps {
  studentId: string;
  studentFirstName: string;
}

const FallAlbumSection: React.FC<FallAlbumSectionProps> = ({
  studentId,
  studentFirstName
}) => {
  const [songs, setSongs] = useState<FirebaseSavedSong[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const fetchSongs = async () => {
      try {
        setLoading(true);
        // Fetch songs for this specific student
        const userSongs = await gameAPI.getUserSongs(studentId);
        setSongs(userSongs);
      } catch (error) {
        console.error('Error fetching student songs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSongs();
  }, [studentId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
      }
    };
  }, [currentAudio]);

  const getGameDataForSong = (song: FirebaseSavedSong) => {
    const game = GAME_REGISTRY[song.gameId];
    return {
      title: game?.title || 'Unknown Game',
      thumbnail: game?.thumbnail || defaultGameImage
    };
  };

  const playAudio = async (song: FirebaseSavedSong) => {
    try {
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setCurrentAudio(null);
      }

      if (!song.audioUrl) {
        console.warn('No audio URL found for song:', song.title);
        return;
      }

      setCurrentlyPlaying(song.id || song.title);
      setIsPlaying(true);

      const audio = new Audio(song.audioUrl);
      setCurrentAudio(audio);
      audioRef.current = audio;

      audio.onloadedmetadata = () => {
        setDuration(audio.duration);
      };

      audio.ontimeupdate = () => {
        setCurrentTime(audio.currentTime);
      };

      audio.onended = () => {
        setCurrentlyPlaying(null);
        setCurrentAudio(null);
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audio.onerror = (error) => {
        console.error('Error playing audio:', error);
        setCurrentlyPlaying(null);
        setCurrentAudio(null);
        setIsPlaying(false);
        setCurrentTime(0);
      };

      await audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      setCurrentlyPlaying(null);
      setCurrentAudio(null);
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const togglePlayPause = (song: FirebaseSavedSong) => {
    if (currentlyPlaying === song.id && currentAudio) {
      if (isPlaying) {
        currentAudio.pause();
        setIsPlaying(false);
      } else {
        currentAudio.play();
        setIsPlaying(true);
      }
    } else {
      playAudio(song);
    }
  };

  const formatTime = (timeInSeconds: number): string => {
    if (isNaN(timeInSeconds) || timeInSeconds < 0) return '0:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  // If no songs, show "coming soon" card
  if (songs.length === 0) {
    return (
      <div className="fall-album-section">
        <h3 className="album-title">🍁 {studentFirstName}'s Fall Album</h3>
        <div className="songs-coming-soon">
          <div className="coming-soon-icon">🎵</div>
          <h4>Songs Coming Soon!</h4>
          <p>{studentFirstName} hasn't created any songs yet, but they're learning the skills to compose their first musical masterpiece. Check back after class!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fall-album-section">
      <h3 className="album-title">🍁 {studentFirstName}'s Fall Album</h3>
      <p className="album-subtitle">{songs.length} {songs.length === 1 ? 'song' : 'songs'} created in class</p>

      <div className="songs-grid">
        {songs.map((song, index) => {
          const gameData = getGameDataForSong(song);
          const isCurrentlyPlaying = currentlyPlaying === song.id;

          return (
            <div
              key={song.id || index}
              className={`song-card ${isCurrentlyPlaying ? 'playing' : ''}`}
            >
              <div className="song-artwork">
                <img src={gameData.thumbnail} alt={gameData.title} />
                <button
                  className="play-overlay"
                  onClick={() => togglePlayPause(song)}
                  aria-label={isCurrentlyPlaying && isPlaying ? 'Pause' : 'Play'}
                >
                  <span className="play-icon">
                    {isCurrentlyPlaying && isPlaying ? '⏸️' : '▶️'}
                  </span>
                </button>
              </div>

              <div className="song-details">
                <h4 className="song-name">{song.title}</h4>
                <p className="song-artist">{studentFirstName}</p>
                <p className="song-game">{gameData.title}</p>
                <p className="song-date">{song.createdAt.toLocaleDateString()}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mini Player for currently playing song */}
      {currentlyPlaying && currentAudio && (
        <div className="mini-player">
          <div className="player-controls">
            <button
              className="player-play-btn"
              onClick={() => {
                const currentSong = songs.find(s => s.id === currentlyPlaying);
                if (currentSong) togglePlayPause(currentSong);
              }}
            >
              {isPlaying ? '⏸️' : '▶️'}
            </button>
            <div className="player-info">
              <div className="player-song-name">
                {songs.find(s => s.id === currentlyPlaying)?.title || 'Unknown'}
              </div>
              <div className="player-progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                />
              </div>
              <div className="player-time">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FallAlbumSection;
